import type { ProductDrawerTab } from "@/components/products/product-inventory-drawer";
import { TablePagination } from "@/components/tables/_components/table-pagination";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useMediaQuery } from "@/hooks/use-media-query";
import { cn } from "@/lib/utils";
import type {
  ProductBrandWarehouseStock,
  Warehouse,
  WarehouseProduct,
} from "@/services/types/products";
import {
  type ExpandedState,
  type FilterFn,
  type Row,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { format } from "date-fns";
import { ChevronDown, ChevronRight, MoreVertical } from "lucide-react";
import { useMemo, useState } from "react";

const columnHelper = createColumnHelper<WarehouseProduct>();

const fuzzyFilter: FilterFn<WarehouseProduct> = (
  row,
  columnId,
  filterValue,
) => {
  const searchValue = filterValue.toLowerCase();
  const sku = String(row.original.sku ?? "").toLowerCase();
  const name = String(row.original.name ?? "").toLowerCase();
  return sku.includes(searchValue) || name.includes(searchValue);
};

function formatQuantity(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return "-";
  }
  return value.toLocaleString();
}

function formatDate(value: string | null | undefined): string {
  if (!value) {
    return "-";
  }
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "-";
    }
    return format(date, "MMM d, yyyy");
  } catch {
    return "-";
  }
}

function getDisplayBaseUom(product: WarehouseProduct): string {
  return product.base_stock_uom_code || "";
}

function getStorageTypeLabel(
  storageType: WarehouseProduct["storage_type"],
): string {
  switch (storageType) {
    case "dry":
      return "Dry Store";
    case "cold":
      return "Cold Store";
    default:
      return "General Store";
  }
}

function getStorageTypeClassName(
  storageType: WarehouseProduct["storage_type"],
): string {
  switch (storageType) {
    case "dry":
      return "bg-amber-100 text-amber-700";
    case "cold":
      return "bg-sky-100 text-sky-700";
    default:
      return "bg-slate-100 text-slate-700";
  }
}

function BrandCountChip({ count }: { count: number }) {
  const label = `${count} ${count === 1 ? "brand" : "brands"}`;

  return (
    <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 font-medium text-[12px] text-slate-700">
      {label}
    </span>
  );
}

function ProductNameCell({ product }: { product: WarehouseProduct }) {
  const brandCount = product.brand_warehouse_stocks.length;

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <span className="truncate text-muted-foreground">
        {product.name ?? "-"}
      </span>
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={cn(
            "inline-flex items-center rounded-full px-2.5 py-1 font-medium text-[12px]",
            getStorageTypeClassName(product.storage_type),
          )}
        >
          {getStorageTypeLabel(product.storage_type)}
        </span>
        {brandCount > 0 ? <BrandCountChip count={brandCount} /> : null}
      </div>
    </div>
  );
}

function BrandStockExpandedPanel({
  stocks,
}: {
  stocks: ProductBrandWarehouseStock[];
}) {
  if (stocks.length === 0) {
    return null;
  }

  return (
    <div className="border-t bg-slate-50/80 px-8 py-6 md:px-14">
      <div className="flex flex-col gap-4">
        {stocks.map((stock) => (
          <div
            className="grid gap-3 text-slate-700 text-sm md:grid-cols-[180px_220px_120px]"
            key={stock.id}
          >
            <div className="flex w-[180px] items-center gap-2 font-medium text-slate-800">
              <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
              <span className="truncate">{stock.brand_name}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-slate-600 text-xs md:text-sm">
                QTY Available
              </span>
              <div className="inline-flex min-w-[64px] items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2 font-medium shadow-sm">
                {formatQuantity(stock.quantity_available)}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-slate-800">
                {formatQuantity(stock.quantity_reserved)}
              </span>
              <span className="text-slate-700">Reserved</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProductRowActions({
  onOpenProduct,
}: {
  onOpenProduct: (tab: ProductDrawerTab) => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 rounded-full text-muted-foreground"
        >
          <MoreVertical className="h-4 w-4" />
          <span className="sr-only">Open product actions</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem>Edit SKU details</DropdownMenuItem>
        <DropdownMenuItem onSelect={() => onOpenProduct("stock")}>
          Edit stock
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => onOpenProduct("units")}>
          Configure UOM
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => onOpenProduct("history")}>
          View stock history
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Grid templates for each screen size
const MOBILE_GRID =
  "minmax(44px, 44px) minmax(180px, 1.6fr) minmax(110px, 1fr)";
const TABLET_GRID =
  "minmax(44px, 44px) minmax(110px, 1fr) minmax(140px, 1.5fr) minmax(90px, 0.9fr) minmax(90px, 0.9fr) minmax(120px, 1fr) minmax(56px, 56px)";
const DESKTOP_GRID =
  "minmax(45px, 45px) minmax(130px, 1.1fr) minmax(150px, 1.5fr) minmax(110px, 1fr) minmax(90px, 0.9fr) minmax(90px, 0.9fr) minmax(130px, 1fr) minmax(140px, 1.1fr) minmax(72px, 72px)";

export function ProductsTable({
  data,
  warehouse,
  onOpenProduct,
}: {
  data: WarehouseProduct[];
  warehouse: Warehouse;
  onOpenProduct: (productId: number, tab: ProductDrawerTab) => void;
}) {
  const isMobile = useMediaQuery("(max-width: 640px)");
  const isTablet = useMediaQuery("(min-width: 641px) and (max-width: 1024px)");
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [selectedStoreFilter, setSelectedStoreFilter] = useState("all");

  const mobileColumns = [
    columnHelper.display({
      id: "expander",
      cell: ({ row }) =>
        row.original.brand_warehouse_stocks.length !== 0 ? (
          <Button
            variant="ghost"
            onClick={() => row.toggleExpanded()}
            className="h-8 w-8 p-0"
          >
            {row.getIsExpanded() ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </Button>
        ) : (
          <div />
        ),
      header: "",
      size: 44,
    }),
    columnHelper.display({
      id: "sku",
      header: "SKU ID",
      cell: ({ row }) => {
        const product = row.original;
        const displayBaseUom = getDisplayBaseUom(product);

        return (
          <div className="flex min-w-0 flex-col gap-2 py-1">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-medium text-foreground">
                  {product.sku || "-"}
                </div>
                <div className="mt-1">
                  <ProductNameCell product={product} />
                </div>
              </div>
              <ProductRowActions
                onOpenProduct={(tab) => onOpenProduct(product.product_id, tab)}
              />
            </div>
            <div>
              {displayBaseUom ? (
                <span className="inline-flex rounded-md border border-border bg-background px-3 py-1.5 text-muted-foreground text-sm">
                  {displayBaseUom}
                </span>
              ) : (
                <button
                  type="button"
                  className="inline-flex whitespace-nowrap rounded-md border border-border bg-muted/40 px-3 py-1.5 text-muted-foreground text-sm hover:bg-muted/80"
                  onClick={() => onOpenProduct(product.product_id, "units")}
                >
                  + Set Up UOM
                </button>
              )}
            </div>
          </div>
        );
      },
    }),
    columnHelper.accessor("quantity_available", {
      cell: ({ row }) => {
        const product = row.original;
        return (
          <div className="flex flex-col items-end gap-0.5 py-1 text-xs">
            <span className="text-muted-foreground">
              <span className="text-foreground/70">Available:</span>{" "}
              {formatQuantity(product.quantity_available)}
            </span>
            <span className="text-muted-foreground">
              <span className="text-foreground/70">Reserved:</span>{" "}
              {formatQuantity(product.quantity_reserved)}
            </span>
            <span className="text-muted-foreground">
              <span className="text-foreground/70">For Sale:</span>{" "}
              {formatQuantity(product.quantity_available_for_sale)}
            </span>
          </div>
        );
      },
      header: "QTY",
    }),
  ];

  const tabletColumns = [
    columnHelper.display({
      id: "expander",
      cell: ({ row }) =>
        row.original.brand_warehouse_stocks.length !== 0 ? (
          <Button
            variant="ghost"
            onClick={() => row.toggleExpanded()}
            className="h-8 w-8 p-0"
          >
            {row.getIsExpanded() ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </Button>
        ) : (
          <div />
        ),
      header: "",
      size: 44,
    }),
    columnHelper.accessor("sku", {
      cell: (info) => (
        <span className="font-medium text-foreground">
          {info.getValue() ?? "-"}
        </span>
      ),
      header: "SKU ID",
    }),
    columnHelper.accessor("name", {
      cell: ({ row }) => <ProductNameCell product={row.original} />,
      header: "SKU Name",
    }),
    columnHelper.accessor("quantity_available", {
      cell: (info) => (
        <span className="text-muted-foreground">
          {formatQuantity(info.getValue())}
        </span>
      ),
      header: "QTY Available",
    }),
    columnHelper.accessor("quantity_available_for_sale", {
      cell: (info) => (
        <span className="text-muted-foreground">
          {formatQuantity(info.getValue())}
        </span>
      ),
      header: "QTY for Sale",
    }),
    columnHelper.display({
      id: "uom",
      header: "Base UOM",
      cell: ({ row }) => {
        const product = row.original;
        const displayBaseUom = getDisplayBaseUom(product);

        if (displayBaseUom) {
          return (
            <span className="inline-flex rounded-md border border-border bg-background px-3 py-1.5 text-muted-foreground text-sm">
              {displayBaseUom}
            </span>
          );
        }

        return (
          <button
            type="button"
            className="inline-flex whitespace-nowrap rounded-md border border-border bg-muted/40 px-3 py-1.5 text-muted-foreground text-sm hover:bg-muted/80"
            onClick={() => onOpenProduct(product.product_id, "units")}
          >
            + Set Up UOM
          </button>
        );
      },
    }),
    columnHelper.display({
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <div className="flex justify-end">
          <ProductRowActions
            onOpenProduct={(tab) => onOpenProduct(row.original.product_id, tab)}
          />
        </div>
      ),
    }),
  ];

  const desktopColumns = [
    columnHelper.display({
      id: "expander",
      cell: ({ row }) =>
        row.original.brand_warehouse_stocks.length !== 0 ? (
          <Button
            variant="ghost"
            onClick={() => row.toggleExpanded()}
            className="h-8 w-8 p-0"
          >
            {row.getIsExpanded() ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </Button>
        ) : (
          <div />
        ),
      header: "",
      size: 45,
    }),
    columnHelper.accessor("sku", {
      cell: (info) => (
        <span className="font-medium text-foreground">
          {info.getValue() ?? "-"}
        </span>
      ),
      header: "SKU ID",
    }),
    columnHelper.accessor("name", {
      cell: ({ row }) => <ProductNameCell product={row.original} />,
      header: "SKU Name",
    }),
    columnHelper.accessor("quantity_available", {
      cell: (info) => (
        <span className="text-muted-foreground">
          {formatQuantity(info.getValue())}
        </span>
      ),
      header: "QTY Available",
    }),
    columnHelper.accessor("quantity_reserved", {
      cell: (info) => (
        <span className="text-muted-foreground">
          {formatQuantity(info.getValue())}
        </span>
      ),
      header: "QTY Reserved",
    }),
    columnHelper.accessor("quantity_available_for_sale", {
      cell: (info) => (
        <span className="text-muted-foreground">
          {formatQuantity(info.getValue())}
        </span>
      ),
      header: "QTY Available for Sale",
    }),
    columnHelper.display({
      id: "uom",
      header: "Base UOM",
      cell: ({ row }) => {
        const product = row.original;
        const displayBaseUom = getDisplayBaseUom(product);

        if (displayBaseUom) {
          return (
            <span className="inline-flex rounded-md border border-border bg-background px-3 py-1.5 text-muted-foreground text-sm">
              {displayBaseUom}
            </span>
          );
        }

        return (
          <button
            type="button"
            className="inline-flex whitespace-nowrap rounded-md border border-border bg-muted/40 px-3 py-1.5 text-muted-foreground text-sm hover:bg-muted/80"
            onClick={() => onOpenProduct(product.product_id, "units")}
          >
            + Set Up UOM
          </button>
        );
      },
    }),
    columnHelper.accessor("updated_at", {
      cell: (info) => (
        <span className="text-muted-foreground text-sm">
          {formatDate(info.getValue())}
        </span>
      ),
      header: "Last Updated",
    }),
    columnHelper.display({
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <div className="flex justify-end">
          <ProductRowActions
            onOpenProduct={(tab) => onOpenProduct(row.original.product_id, tab)}
          />
        </div>
      ),
    }),
  ];

  const getColumns = () => {
    if (isMobile) {
      return mobileColumns;
    }
    if (isTablet) {
      return tabletColumns;
    }
    return desktopColumns;
  };

  const getGridTemplateColumns = () => {
    if (isMobile) {
      return MOBILE_GRID;
    }
    if (isTablet) {
      return TABLET_GRID;
    }
    return DESKTOP_GRID;
  };

  const getProductCount = (storeType: string) =>
    data.reduce(
      (accumulator, currentItem) =>
        accumulator + (currentItem.storage_type === storeType ? 1 : 0),
      0,
    );

  const filteredProducts = useMemo(
    () =>
      data.filter(
        (item: WarehouseProduct) =>
          selectedStoreFilter === "all" ||
          item.storage_type === selectedStoreFilter,
      ),
    [data, selectedStoreFilter],
  );

  const table = useReactTable({
    data: filteredProducts,
    columns: getColumns(),
    state: {
      expanded,
    },
    onExpandedChange: setExpanded,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    globalFilterFn: fuzzyFilter,
  });

  const ExpandedRow = ({
    row,
  }: {
    row: Row<WarehouseProduct>;
  }) => {
    const product = row.original;
    return <BrandStockExpandedPanel stocks={product.brand_warehouse_stocks} />;
  };

  return (
    <>
      <div className="rounded-md border">
        <div className="flex flex-col justify-between gap-4 p-4 md:flex-row md:items-center md:p-6">
          <div className="flex items-center gap-2">
            <span className="font-medium text-lg md:text-xl">
              {warehouse.name || "Warehouse"}
            </span>
            <Badge className="bg-brand-yellowGreen text-brand-darkGreen text-xs">
              {`${table.getFilteredRowModel().rows.length} products`}
            </Badge>
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              className={`cursor-pointer rounded-xl p-4 pt-1 pb-1 text-xs ${
                selectedStoreFilter === "all"
                  ? "bg-foreground text-background"
                  : "bg-gray-200 text-gray-600"
              }`}
              onClick={() => setSelectedStoreFilter("all")}
            >
              All ({data.length})
            </button>
            <button
              type="button"
              className={`cursor-pointer rounded-xl p-4 pt-1 pb-1 text-xs ${
                selectedStoreFilter === "general"
                  ? "bg-foreground text-background"
                  : "bg-gray-200 text-gray-600"
              }`}
              onClick={() => setSelectedStoreFilter("general")}
            >
              General ({getProductCount("general")})
            </button>
            <button
              type="button"
              className={`cursor-pointer rounded-xl p-4 pt-1 pb-1 text-xs ${
                selectedStoreFilter === "cold"
                  ? "bg-foreground text-background"
                  : "bg-gray-200 text-gray-600"
              }`}
              onClick={() => setSelectedStoreFilter("cold")}
            >
              Cold ({getProductCount("cold")})
            </button>
            <button
              type="button"
              className={`cursor-pointer rounded-xl p-4 pt-1 pb-1 text-xs ${
                selectedStoreFilter === "dry"
                  ? "bg-foreground text-background"
                  : "bg-gray-200 text-gray-600"
              }`}
              onClick={() => setSelectedStoreFilter("dry")}
            >
              Dry ({getProductCount("dry")})
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <div
            className="grid items-center border-b bg-muted font-medium text-sm"
            style={{ gridTemplateColumns: getGridTemplateColumns() }}
          >
            {table.getFlatHeaders().map((header) => (
              <div key={header.id} className="p-3">
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext(),
                )}
              </div>
            ))}
          </div>

          {table.getRowModel().rows.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              No products found
            </div>
          ) : (
            table.getRowModel().rows.map((row) => {
              const isExpanded = row.getIsExpanded();
              return (
                <div
                  key={row.id}
                  className="border-b bg-card text-card-foreground"
                >
                  <div
                    className="grid items-center transition-colors hover:bg-muted/50"
                    style={{ gridTemplateColumns: getGridTemplateColumns() }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <div key={cell.id} className="truncate p-3">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </div>
                    ))}
                  </div>
                  {isExpanded ? <ExpandedRow row={row} /> : null}
                </div>
              );
            })
          )}
        </div>

        <TablePagination
          currentPage={table.getState().pagination.pageIndex}
          pageCount={table.getPageCount() || 1}
          canPreviousPage={table.getCanPreviousPage()}
          canNextPage={table.getCanNextPage()}
          onPreviousPage={() => table.previousPage()}
          onNextPage={() => table.nextPage()}
          onPageChange={(pageIndex) => table.setPageIndex(pageIndex)}
        />
      </div>
    </>
  );
}
